import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Eraser, PenLine } from 'lucide-react';

export const SIGNATURE_MIN_POINTS = 30;
export const SIGNATURE_MIN_EXTENT = 60;

export const signatureIsValid = (v) => {
  if (!v || !Array.isArray(v.strokes)) return false;
  const pts = v.strokes.flat();
  if (pts.length < SIGNATURE_MIN_POINTS) return false;
  const xs = pts.map((p) => p[0]); const ys = pts.map((p) => p[1]);
  return Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) >= SIGNATURE_MIN_EXTENT;
};

const SignaturePad = ({ value, onChange, invalid, id }) => {
  const canvasRef = useRef(null);
  const strokesRef = useRef(value?.strokes || []);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState((value?.strokes || []).length > 0);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round'; ctx.lineJoin = 'round'; ctx.lineWidth = 2.2; ctx.strokeStyle = '#0A1551';
    strokesRef.current.forEach((s) => {
      if (s.length < 2) return;
      ctx.beginPath(); ctx.moveTo(s[0][0], s[0][1]);
      for (let i = 1; i < s.length; i++) {
        const mx = (s[i - 1][0] + s[i][0]) / 2, my = (s[i - 1][1] + s[i][1]) / 2;
        ctx.quadraticCurveTo(s[i - 1][0], s[i - 1][1], mx, my);
      }
      ctx.stroke();
    });
  }, []);

  useEffect(() => { redraw(); window.addEventListener('resize', redraw); return () => window.removeEventListener('resize', redraw); }, [redraw]);

  const pos = (e) => { const r = canvasRef.current.getBoundingClientRect(); return [Math.round(e.clientX - r.left), Math.round(e.clientY - r.top)]; };
  const emit = () => {
    const strokes = strokesRef.current;
    const pts = strokes.flat();
    onChange(strokes.length ? { strokes, points: pts.length, width: canvasRef.current.getBoundingClientRect().width, height: canvasRef.current.getBoundingClientRect().height, signed_at: new Date().toISOString() } : null);
  };

  const down = (e) => { e.preventDefault(); canvasRef.current.setPointerCapture(e.pointerId); drawingRef.current = true; strokesRef.current = [...strokesRef.current, [pos(e)]]; setHasInk(true); };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const cur = strokesRef.current[strokesRef.current.length - 1];
    cur.push(pos(e));
    const ctx = canvasRef.current.getContext('2d');
    const n = cur.length;
    if (n >= 2) { ctx.beginPath(); ctx.moveTo(cur[n - 2][0], cur[n - 2][1]); ctx.lineTo(cur[n - 1][0], cur[n - 1][1]); ctx.stroke(); }
  };
  const up = (e) => { if (!drawingRef.current) return; drawingRef.current = false; try { canvasRef.current.releasePointerCapture(e.pointerId); } catch (err) { console.debug('[signature] releasePointerCapture skipped', err); } emit(); };
  const clear = () => { strokesRef.current = []; setHasInk(false); redraw(); onChange(null); };

  return (
    <div data-testid={`signature-pad-${id}`}>
      <div className={`relative rounded-lg border-2 ${invalid ? 'border-[#E5387B] bg-[#FFF5F7]' : 'border-dashed border-[#B8C0E0] bg-white'} overflow-hidden`}>
        <canvas
          ref={canvasRef}
          className="w-full h-[160px] block cursor-crosshair"
          style={{ touchAction: 'none' }}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          onPointerLeave={up}
          aria-label="Signature drawing area"
          role="img"
          data-testid={`signature-canvas-${id}`}
        />
        {!hasInk && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 text-[13px] text-[#9599C0] pointer-events-none">
            <PenLine className="w-4 h-4" /> Draw your signature with your mouse, finger or pen
          </div>
        )}
        <div className="absolute left-4 right-4 bottom-8 border-b border-[#D5D7ED] pointer-events-none" />
      </div>
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[12px] text-[#6F76A7]">{hasInk ? (signatureIsValid(value) ? 'Signature captured' : 'Keep going — sign fully') : 'Required: a real handwritten signature'}</span>
        <button type="button" onClick={clear} data-testid={`signature-clear-${id}`} className="flex items-center gap-1 text-[12px] font-medium text-[#0A1551] hover:text-[#E5387B]"><Eraser className="w-3.5 h-3.5" /> Clear</button>
      </div>
    </div>
  );
};

export default SignaturePad;
